import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Typography, Card, Skeleton, Button, Chip, Grid, Stack, Divider,
  Autocomplete, TextField, MenuItem, Select, InputLabel, FormControl,
  Table, TableHead, TableRow, TableCell, TableBody, Alert, IconButton, Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useGetMyStudentsQuery, useGetMyStudentReportCardQuery } from "../../Api/attoDeskApi";

const NAVY = "#1b3a6b";
const NAVY_2 = "#2a5298";
const PAGE_BG = "#f0f4f8";

type ReportCard = {
  student: { id: number; name: string; grade: string | null; reference_id: string | null };
  period: { from: string; to: string; label: string };
  summary: {
    overall_average: number | null;
    graded_count: number;
    passed_count: number;
    pending_count: number;
    subjects_count: number;
    attendance_rate: number | null;
  };
  subjects: Array<{
    subject: string;
    assessments: number;
    passed: number;
    failed: number;
    average: number | null;
    latest: { date: string | null; score: number | null; max_score: number | null; percentage: number | null } | null;
  }>;
  assessments: Array<{
    assessment_id: number;
    date: string | null;
    subject: string;
    title: string;
    type: string | null;
    score: number | null;
    max_score: number | null;
    percentage: number | null;
    marked: boolean;
    result_status: string | null;
    pass_fail: boolean | null;
    teacher: string | null;
  }>;
  feedback: Array<{
    assessment_id: number;
    title: string;
    subject: string;
    date: string | null;
    teacher: string | null;
    overall: string | null;
    strengths: string | null;
    next_steps: string | null;
    analysis: string | null;
  }>;
  attendance: {
    scheduled: number;
    present: number;
    absent: number;
    late: number;
    not_marked: number;
    attendance_rate: number | null;
  };
};

const fmtDate = (iso: string | null) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};
const humanizeType = (t: string | null | undefined): string => {
  if (!t) return "—";
  return t.charAt(0).toUpperCase() + t.slice(1);
};

// Period presets — the endpoint accepts any from/to window; these keep
// the picker minimal and honest to what the codebase actually models
// (no term/semester schema exists yet).
type PeriodKey = "current_month" | "previous_month" | "last_90" | "year_to_date";
const periodRange = (key: PeriodKey): { from: string; to: string; label: string } => {
  const today = new Date();
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (key === "current_month") {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: iso(from), to: iso(to), label: "Current month" };
  }
  if (key === "previous_month") {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: iso(from), to: iso(to), label: "Previous month" };
  }
  if (key === "last_90") {
    const from = new Date(today); from.setDate(from.getDate() - 90);
    return { from: iso(from), to: iso(today), label: "Last 90 days" };
  }
  const from = new Date(today.getFullYear(), 0, 1);
  return { from: iso(from), to: iso(today), label: "Year to date" };
};

const KpiTile: React.FC<{ label: string; value: React.ReactNode; accent: string; bg: string; icon: React.ReactNode }> = ({ label, value, accent, bg, icon }) => (
  <Card sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0", height: "100%", position: "relative", overflow: "hidden",
    "&::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 3, bgcolor: accent },
    className: "kpi-tile",
  }} className="kpi-tile">
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
      <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: bg, color: accent, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 16 } }}>{icon}</Box>
      <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b" }}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>{value}</Typography>
  </Card>
);

const resultChip = (a: ReportCard["assessments"][number]) => {
  if (!a.marked) {
    const isUpcoming = a.date && new Date(a.date).getTime() > Date.now();
    return <Chip size="small" label={isUpcoming ? "Upcoming" : "Not Graded"} sx={{ bgcolor: isUpcoming ? "#f3e8ff" : "#f1f5f9", color: isUpcoming ? "#6b21a8" : "#475569", fontWeight: 700, fontSize: 10.5, height: 22 }} />;
  }
  if (a.result_status === "excused") return <Chip size="small" label="Excused" sx={{ bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 700, fontSize: 10.5, height: 22 }} />;
  if (a.pass_fail === true) return <Chip size="small" label="Passed" sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 800, fontSize: 10.5, height: 22 }} />;
  if (a.pass_fail === false) return <Chip size="small" label="Below Pass" sx={{ bgcolor: "#fee2e2", color: "#b91c1c", fontWeight: 800, fontSize: 10.5, height: 22 }} />;
  return <Chip size="small" label="Graded" sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 700, fontSize: 10.5, height: 22 }} />;
};

const initials = (name: string): string =>
  name.trim().split(/\s+/).map((n) => n[0]).join("").slice(0, 2).toUpperCase() || "?";

const StaffReportCards: React.FC = () => {
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  // Deep-link preselect: /staff-portal/reports/report-cards?student_id=N
  // — set by "View Report Card" on the Staff student profile page.
  const preselectId = Number(urlParams.get("student_id"));
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(
    !preselectId || Number.isNaN(preselectId) ? null : preselectId,
  );
  useEffect(() => {
    if (preselectId && !Number.isNaN(preselectId) && selectedStudentId == null) {
      setSelectedStudentId(preselectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectId]);
  const [periodKey, setPeriodKey] = useState<PeriodKey>("current_month");
  const [subjectFilter, setSubjectFilter] = useState<string>("");

  const period = useMemo(() => periodRange(periodKey), [periodKey]);

  const { data: studentsResp, isLoading: studentsLoading } = useGetMyStudentsQuery();
  const students: Array<{ student_id: number; student_name: string; grade: string | null }> =
    Array.isArray(studentsResp?.data) ? (studentsResp!.data as any[]) : [];

  const { data: cardResp, isFetching: cardLoading, refetch } = useGetMyStudentReportCardQuery(
    { studentId: selectedStudentId ?? 0, from: period.from, to: period.to },
    { skip: !selectedStudentId },
  );
  const card = (cardResp && (cardResp as any).status && (cardResp as any).data) as ReportCard | undefined;

  const filteredAssessments = useMemo(() => {
    if (!card) return [];
    return subjectFilter ? card.assessments.filter((a) => a.subject === subjectFilter) : card.assessments;
  }, [card, subjectFilter]);

  const subjectOptions = card ? Array.from(new Set(card.subjects.map((s) => s.subject))) : [];

  const handlePrint = () => window.print();
  // White-label: header + footer names come from Redux companySlice.
  const companyName = useSelector((s: any) => s?.company?.companyName as string | undefined) ?? "";

  return (
    <Box className="report-card-root" sx={{ bgcolor: PAGE_BG, minHeight: "100vh", pb: 4 }}>
      {/* Print-only Step Up header — hidden on screen */}
      <Box className="print-only" sx={{ display: "none" }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: NAVY }}>{companyName || ""}</Typography>
        <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1 }}>Student Report Card</Typography>
        <Divider sx={{ mb: 1.5 }} />
      </Box>

      {/* Print CSS. Uses .no-print to strip filters/actions, keeps the
          report-card body clean on paper. */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .report-card-root { background: #fff !important; }
          .kpi-tile { break-inside: avoid; page-break-inside: avoid; }
          .rc-card { break-inside: avoid; page-break-inside: avoid; box-shadow: none !important; border: 1px solid #e2e8f0 !important; }
          .MuiTableCell-root { color: #000 !important; }
        }
      `}</style>

      {/* Header */}
      <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 2, md: 3 } }}>
        <Card sx={{
          p: { xs: 2.5, md: 3 }, borderRadius: 3, border: "1px solid #e2e8f0",
          background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_2} 100%)`, color: "#fff",
        }} className="rc-card">
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", opacity: 0.8 }}>STUDENT REPORTING</Typography>
              <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, letterSpacing: "-0.01em" }}>Report Cards</Typography>
              <Typography sx={{ fontSize: 13, opacity: 0.85, mt: 0.5, maxWidth: 620 }}>
                Review academic progress, assessment results, attendance, and teacher feedback for students in your teaching scope.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} className="no-print">
              <Button
                variant="contained"
                startIcon={<PrintOutlinedIcon />}
                onClick={handlePrint}
                disabled={!card}
                sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#fff", color: NAVY, "&:hover": { bgcolor: "#f1f5f9" }, "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.4)", color: "#e2e8f0" } }}
              >
                Print Report Card
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Box>

      {/* Filter bar */}
      <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }} className="no-print">
        <Card sx={{ p: 2, borderRadius: 3, border: "1px solid #e2e8f0" }} className="rc-card">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Autocomplete
                options={students}
                loading={studentsLoading}
                getOptionLabel={(o) => `${o.student_name}${o.grade ? ` — Grade ${o.grade}` : ""}`}
                value={students.find((s) => s.student_id === selectedStudentId) || null}
                onChange={(_e, val) => setSelectedStudentId(val?.student_id ?? null)}
                renderInput={(params) => <TextField {...params} size="small" label="Student" placeholder="Search your students…" />}
                isOptionEqualToValue={(o, v) => o.student_id === v.student_id}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Academic Period</InputLabel>
                <Select value={periodKey} label="Academic Period" onChange={(e) => setPeriodKey(e.target.value as PeriodKey)}>
                  <MenuItem value="current_month">Current Month</MenuItem>
                  <MenuItem value="previous_month">Previous Month</MenuItem>
                  <MenuItem value="last_90">Last 90 Days</MenuItem>
                  <MenuItem value="year_to_date">Year to Date</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" disabled={!card || subjectOptions.length === 0}>
                <InputLabel>Subject</InputLabel>
                <Select value={subjectFilter} label="Subject" onChange={(e) => setSubjectFilter(e.target.value)}>
                  <MenuItem value="">All Subjects</MenuItem>
                  {subjectOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1} sx={{ textAlign: "right" }}>
              <Tooltip title="Refresh">
                <span>
                  <IconButton onClick={() => refetch()} disabled={!selectedStudentId} sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
                    <RefreshOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* Empty state — no student picked */}
      {!selectedStudentId && (
        <Box sx={{ px: { xs: 2, md: 4 }, mt: 3 }}>
          <Card sx={{ p: 6, textAlign: "center", borderRadius: 3, border: "1px solid #e2e8f0" }} className="rc-card">
            <Box sx={{ width: 56, height: 56, mx: "auto", borderRadius: "50%", bgcolor: "#eef2ff", color: NAVY, display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a", mb: 0.5 }}>Select a student to view their report card</Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", maxWidth: 480, mx: "auto" }}>
              Choose one of your assigned students to review academic performance, attendance, and feedback.
            </Typography>
          </Card>
        </Box>
      )}

      {/* Loading skeletons */}
      {selectedStudentId && cardLoading && !card && (
        <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
          <Skeleton variant="rounded" height={110} sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Grid item xs={6} md={2.4} key={i}><Skeleton variant="rounded" height={90} /></Grid>
            ))}
          </Grid>
          <Skeleton variant="rounded" height={220} />
        </Box>
      )}

      {/* Report card body */}
      {selectedStudentId && card && (
        <>
          {/* Student profile summary */}
          <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
            <Card sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0" }} className="rc-card">
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: NAVY, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18, flexShrink: 0 }}>
                    {initials(card.student.name)}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{card.student.name}</Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 0.25 }}>
                      {card.student.grade && <Chip size="small" label={`Grade ${card.student.grade}`} sx={{ bgcolor: "#eef2ff", color: NAVY, fontWeight: 700, fontSize: 11 }} />}
                      {card.student.reference_id && <Chip size="small" label={card.student.reference_id} sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 700, fontSize: 11 }} />}
                      <Chip size="small" label={card.period.label} sx={{ bgcolor: "#ecfeff", color: "#0891b2", fontWeight: 700, fontSize: 11 }} />
                    </Stack>
                  </Box>
                </Stack>
                <Button
                  className="no-print"
                  variant="outlined"
                  onClick={() => navigate(`/staff-portal/students/${card.student.id}`)}
                  startIcon={<PersonOutlineIcon />}
                  sx={{ textTransform: "none", fontWeight: 700, borderColor: NAVY, color: NAVY }}
                >
                  View Student Profile
                </Button>
              </Stack>
            </Card>
          </Box>

          {/* KPI row */}
          <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={2.4}><KpiTile label="Overall Average" value={card.summary.overall_average !== null ? `${card.summary.overall_average}%` : "—"} accent={NAVY} bg="#eef2ff" icon={<TrendingUpOutlinedIcon />} /></Grid>
              <Grid item xs={6} md={2.4}><KpiTile label="Assessments Graded" value={card.summary.graded_count} accent="#7c3aed" bg="#f5f3ff" icon={<ChecklistOutlinedIcon />} /></Grid>
              <Grid item xs={6} md={2.4}><KpiTile label="Passed" value={card.summary.passed_count} accent="#16a34a" bg="#f0fdf4" icon={<CheckCircleOutlineIcon />} /></Grid>
              <Grid item xs={6} md={2.4}><KpiTile label="Attendance" value={card.attendance.attendance_rate !== null ? `${card.attendance.attendance_rate}%` : "—"} accent="#0891b2" bg="#ecfeff" icon={<EventAvailableOutlinedIcon />} /></Grid>
              <Grid item xs={6} md={2.4}><KpiTile label="Late / Absent" value={`${card.attendance.late} Late · ${card.attendance.absent} Absent`} accent={card.attendance.absent > 0 || card.attendance.late > 0 ? "#e11d48" : "#94a3b8"} bg="#fef2f2" icon={<CancelOutlinedIcon />} /></Grid>
            </Grid>
          </Box>

          {/* Subject performance */}
          <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
            <Card sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0" }} className="rc-card">
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.5 }}>Subject Performance</Typography>
              {card.subjects.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No assessments recorded in this period yet.</Typography>
              ) : (
                <Grid container spacing={2}>
                  {card.subjects.map((s) => (
                    <Grid item xs={12} sm={6} md={4} key={s.subject}>
                      <Box sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2, height: "100%" }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{s.subject}</Typography>
                          <Typography sx={{ fontSize: 18, fontWeight: 800, color: s.average === null ? "#94a3b8" : s.average >= 80 ? "#16a34a" : s.average >= 60 ? "#0891b2" : "#e11d48" }}>
                            {s.average !== null ? `${s.average}%` : "—"}
                          </Typography>
                        </Stack>
                        {s.average === null ? (
                          <Typography sx={{ fontSize: 12, color: "text.secondary", fontStyle: "italic" }}>No graded assessments yet</Typography>
                        ) : (
                          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                            Assessments: {s.assessments} · Passed: {s.passed}{s.failed > 0 ? ` · Below Pass: ${s.failed}` : ""}
                          </Typography>
                        )}
                        {s.latest && s.latest.score !== null && s.latest.max_score !== null && (
                          <Typography sx={{ fontSize: 11, color: "#475569", mt: 0.5 }}>
                            Latest: {s.latest.score} / {s.latest.max_score}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Card>
          </Box>

          {/* Assessment results */}
          <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
            <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }} className="rc-card">
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Assessment Results</Typography>
              </Box>
              {filteredAssessments.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No graded assessments yet. Results will appear here once grading is completed.</Typography>
                </Box>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: NAVY }}>
                        {["Date", "Subject", "Assessment", "Type", "Score", "%", "Result", "Teacher"].map((h, i) => (
                          <TableCell key={i} sx={{ color: "#ffffff !important", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: "none", "& *": { color: "#ffffff !important" } }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAssessments.map((a, idx) => (
                        <TableRow key={a.assessment_id} hover onClick={() => navigate(`/staff-portal/assessments/${a.assessment_id}`)}
                          sx={{ cursor: "pointer", bgcolor: idx % 2 === 0 ? "#fff" : "#fafafa", "&:hover": { bgcolor: "#f0f4ff" } }}>
                          <TableCell sx={{ fontSize: 12.5, color: NAVY, fontWeight: 600 }}>{fmtDate(a.date)}</TableCell>
                          <TableCell sx={{ fontSize: 12.5 }}><Chip size="small" label={a.subject} sx={{ bgcolor: "#eff6ff", color: "#2563eb", fontWeight: 600, fontSize: 11, height: 22 }} /></TableCell>
                          <TableCell sx={{ fontSize: 12.5, color: "#1a1a2e", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: "#475569" }}>{humanizeType(a.type)}</TableCell>
                          <TableCell sx={{ fontSize: 12.5, fontWeight: 700 }}>{a.marked && a.score !== null && a.max_score !== null ? `${a.score} / ${a.max_score}` : "—"}</TableCell>
                          <TableCell sx={{ fontSize: 12.5, fontWeight: 700 }}>{a.marked && a.percentage !== null ? `${Math.round(a.percentage)}%` : "—"}</TableCell>
                          <TableCell>{resultChip(a)}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: "#475569" }}>{a.teacher || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Card>
          </Box>

          {/* Teacher feedback */}
          <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
            <Card sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0" }} className="rc-card">
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <PsychologyOutlinedIcon sx={{ color: NAVY }} />
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Teacher Feedback</Typography>
              </Stack>
              {card.feedback.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: "text.secondary", fontStyle: "italic" }}>No teacher feedback has been recorded yet.</Typography>
              ) : (
                <Stack spacing={2}>
                  {card.feedback.slice(0, 6).map((f) => (
                    <Box key={f.assessment_id} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{f.title} <Box component="span" sx={{ color: "#64748b", fontWeight: 500 }}>· {f.subject}</Box></Typography>
                        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{fmtDate(f.date)}{f.teacher ? ` · ${f.teacher}` : ""}</Typography>
                      </Stack>
                      {f.overall && <FeedbackRow label="Overall Feedback" body={f.overall} />}
                      {f.strengths && <FeedbackRow label="Strengths" body={f.strengths} />}
                      {f.next_steps && <FeedbackRow label="Next Steps" body={f.next_steps} />}
                      {f.analysis && <FeedbackRow label="Teacher Analysis" body={f.analysis} />}
                    </Box>
                  ))}
                  {card.feedback.length > 6 && (
                    <Typography sx={{ fontSize: 11.5, color: "text.secondary", textAlign: "center" }}>Showing 6 of {card.feedback.length} entries.</Typography>
                  )}
                </Stack>
              )}
            </Card>
          </Box>

          {/* Attendance summary + Academic snapshot */}
          <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }} className="rc-card">
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.5 }}>Attendance Summary</Typography>
                  {card.attendance.scheduled === 0 ? (
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No attendance has been submitted for this period.</Typography>
                  ) : (
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}><Metric label="Scheduled" value={card.attendance.scheduled} /></Grid>
                      <Grid item xs={6}><Metric label="Present" value={card.attendance.present} color="#16a34a" /></Grid>
                      <Grid item xs={6}><Metric label="Absent" value={card.attendance.absent} color={card.attendance.absent > 0 ? "#b91c1c" : undefined} /></Grid>
                      <Grid item xs={6}><Metric label="Late" value={card.attendance.late} color={card.attendance.late > 0 ? "#d97706" : undefined} /></Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>Attendance Rate</Typography>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: (card.attendance.attendance_rate ?? 0) >= 90 ? "#16a34a" : (card.attendance.attendance_rate ?? 0) >= 75 ? "#d97706" : "#b91c1c" }}>
                          {card.attendance.attendance_rate !== null ? `${card.attendance.attendance_rate}%` : "—"}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  <Button className="no-print" size="small" onClick={() => navigate("/attendance-record/report")} sx={{ textTransform: "none", fontWeight: 700, color: NAVY_2, mt: 1 }}>
                    View Attendance Report →
                  </Button>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }} className="rc-card">
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.5 }}>Academic Summary</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}><Metric label="Overall Average" value={card.summary.overall_average !== null ? `${card.summary.overall_average}%` : "—"} /></Grid>
                    <Grid item xs={6}><Metric label="Subjects Studied" value={card.summary.subjects_count} /></Grid>
                    <Grid item xs={6}><Metric label="Completed" value={card.summary.graded_count} color="#16a34a" /></Grid>
                    <Grid item xs={6}><Metric label="Pending" value={card.summary.pending_count} color={card.summary.pending_count > 0 ? "#d97706" : undefined} /></Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ px: { xs: 2, md: 4 }, mt: 3 }} className="print-only">
            <Divider sx={{ mb: 1 }} />
            <Typography sx={{ fontSize: 10.5, color: "#64748b", textAlign: "center" }}>
              {companyName ? `Generated by ${companyName} · ` : "Generated "}{new Date().toLocaleString()}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

const FeedbackRow: React.FC<{ label: string; body: string }> = ({ label, body }) => (
  <Box sx={{ mb: 1 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", mb: 0.25 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, color: "#1e293b", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{body}</Typography>
  </Box>
);

const Metric: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
  <Box>
    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", mb: 0.25 }}>{label}</Typography>
    <Typography sx={{ fontSize: 16, fontWeight: 800, color: color ?? "#0f172a" }}>{value}</Typography>
  </Box>
);

export default StaffReportCards;
